import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { trackLanguageChange, trackButtonClick } from '../utils/gtag';
import BookingWidget from './BookingWidget';

/**
 * Header Component
 */
const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [isSticky, setIsSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  // Desktop Sticky Header with Booking Widget
  if (isSticky && !isMobile) {
    return (
      <header className="fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 bg-white shadow-xl py-2">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/inc/images/logo.svg`}
                alt="Royal Rooms B&B"
                className="h-12 w-auto"
              />
            </Link>
            {/* Booking Widget */}
            <div className="flex-1">
              <BookingWidget variant="sticky" />
            </div>
          </div>
        </nav>
      </header>
    );
  }

  // Main Header
  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 bg-gradient-to-r from-primary-bronze via-[#7a5230] to-primary-bronze shadow-lg py-3 sm:py-4">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center transition-transform duration-300 hover:scale-105"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/inc/images/logo.png`}
              alt="Royal Rooms B&B"
              className="h-12 sm:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center gap-6 xl:gap-8">
            <li>
              <Link
                to="/"
                className={`text-white font-medium hover:text-cream transition-colors ${isActive('/') ? 'text-cream' : ''}`}
              >
                {t('nav.home')}
              </Link>
            </li>
            <li>
              <Link
                to="/rooms"
                className={`text-white font-medium hover:text-cream transition-colors ${isActive('/rooms') ? 'text-cream' : ''}`}
              >
                {t('nav.rooms')}
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={`text-white font-medium hover:text-cream transition-colors ${isActive('/about') ? 'text-cream' : ''}`}
              >
                {t('nav.about')}
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className={`text-white font-medium hover:text-cream transition-colors ${isActive('/contact') ? 'text-cream' : ''}`}
              >
                {t('nav.contact')}
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <button
                onClick={() => { changeLanguage('it'); trackLanguageChange('it'); }}
                className={`px-3 py-1.5 rounded border-2 text-sm font-semibold transition-all ${
                  language === 'it'
                    ? 'bg-white text-primary-bronze border-white'
                    : 'bg-transparent text-white border-white hover:bg-white hover:text-primary-bronze'
                }`}
              >
                IT
              </button>
              <button
                onClick={() => { changeLanguage('en'); trackLanguageChange('en'); }}
                className={`px-3 py-1.5 rounded border-2 text-sm font-semibold transition-all ${
                  language === 'en'
                    ? 'bg-white text-primary-bronze border-white'
                    : 'bg-transparent text-white border-white hover:bg-white hover:text-primary-bronze'
                }`}
              >
                EN
              </button>
            </li>
            <li>
              <a
                href="https://royalroomsbbnapoli.kross.travel/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackButtonClick('Book Now', 'header')}
                className="bg-white text-black px-4 py-2 rounded font-semibold text-sm hover:bg-cream transition-colors shadow-md"
              >
                {t('nav.bookNow')}
              </a>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              {isMobileMenuOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-white/20 mt-3 pt-4">
            <ul className="flex flex-col gap-3">
              <li>
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className={`block text-white font-medium py-2 ${isActive('/') ? 'bg-white/20 px-3 rounded' : ''}`}>
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/rooms" onClick={() => setIsMobileMenuOpen(false)} className={`block text-white font-medium py-2 ${isActive('/rooms') ? 'bg-white/20 px-3 rounded' : ''}`}>
                  {t('nav.rooms')}
                </Link>
              </li>
              <li>
                <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className={`block text-white font-medium py-2 ${isActive('/about') ? 'bg-white/20 px-3 rounded' : ''}`}>
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className={`block text-white font-medium py-2 ${isActive('/contact') ? 'bg-white/20 px-3 rounded' : ''}`}>
                  {t('nav.contact')}
                </Link>
              </li>
              <li className="flex items-center gap-2 py-2">
                <button
                  onClick={() => { changeLanguage('it'); trackLanguageChange('it'); }}
                  className={`px-3 py-1.5 rounded border-2 text-sm font-semibold ${language === 'it' ? 'bg-white text-primary-bronze border-white' : 'bg-transparent text-white border-white'}`}
                >
                  IT
                </button>
                <button
                  onClick={() => { changeLanguage('en'); trackLanguageChange('en'); }}
                  className={`px-3 py-1.5 rounded border-2 text-sm font-semibold ${language === 'en' ? 'bg-white text-primary-bronze border-white' : 'bg-transparent text-white border-white'}`}
                >
                  EN
                </button>
              </li>
              <li>
                <a
                  href="https://royalroomsbbnapoli.kross.travel/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white text-black px-4 py-2 rounded font-semibold text-sm text-center"
                >
                  {t('nav.bookNow')}
                </a>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
